import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStockItems, useDebounce } from '@saiens/shared/hooks';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { StockItem } from '@saiens/shared/types';
import {
  Search,
  Plus,
  Package,
  MapPin,
  Ruler,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'in_stock', label: '在庫' },
  { value: 'reserved', label: '已預定' },
  { value: 'scrapped', label: '已報廢' },
];

const STATUS_LABELS: Record<string, string> = {
  in_stock: '在庫',
  reserved: '已預定',
  scrapped: '已報廢',
};

const STATUS_COLORS: Record<string, string> = {
  in_stock: 'bg-green-500 text-white',
  reserved: 'bg-orange-400 text-white',
  scrapped: 'bg-red-500 text-white',
};

const BRAND_COLORS: Record<string, string> = {
  QJ: 'bg-pink-500 text-white',
  MQ: 'bg-blue-500 text-white',
  EG: 'bg-green-600 text-white',
  ET: 'bg-purple-500 text-white',
};

const THICKNESS_LABELS: Record<string, string> = {
  '10': '10mm', '12': '12mm', '15': '15mm',
  '17': '17mm', '20': '20mm', '30': '30mm',
};

function getBrandColor(brand: string): string {
  return BRAND_COLORS[brand] || 'bg-stone-500 text-white';
}

const LIMIT = 20;

export function StockListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);

  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading, error } = useStockItems({
    search: debouncedSearch || undefined,
    status: status || undefined,
    offset,
    limit: LIMIT,
  });

  const handleStatusChange = (value: string) => {
    setStatus(value);
    setOffset(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setOffset(0);
  };

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;
  const currentPage = Math.floor(offset / LIMIT) + 1;

  return (
    <div className="px-4 py-6 pb-24">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">餘料庫存</h1>
          <p className="text-sm text-gray-500">石材餘料管理</p>
        </div>
        {data && (
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
            共 {data.total} 筆
          </span>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder="搜尋編號、型號、案件編號..."
          className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:border-primary focus:outline-none"
        />
      </div>

      {/* Status filter chips */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => handleStatusChange(opt.value)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              status === opt.value
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-2">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="rounded-lg bg-white py-12 text-center text-red-500 shadow-sm">
            載入失敗
          </div>
        ) : !data?.items.length ? (
          <div className="rounded-lg bg-white py-12 text-center text-gray-500 shadow-sm">
            <Package className="mx-auto mb-2 h-8 w-8 text-gray-300" />
            沒有符合條件的餘料
          </div>
        ) : (
          data.items.map((item: StockItem) => (
            <Link
              key={item.id}
              to={`/stock/${item.id}`}
              className="block overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Card header with brand + code */}
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${getBrandColor(item.brand_code)}`}>
                    {item.brand_code}
                  </span>
                  <span className="font-mono text-sm font-semibold text-gray-900">
                    {item.code}
                  </span>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_COLORS[item.status] || 'bg-gray-400 text-white'}`}>
                  {STATUS_LABELS[item.status] || item.status}
                </span>
              </div>

              {/* Card body */}
              <div className="px-3 py-2.5">
                <p className="text-sm font-medium text-gray-800">{item.model_number}</p>

                {/* Dimensions + Location row */}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Ruler className="h-3 w-3 text-gray-400" />
                    {item.long_side} × {item.short_side} mm
                    <span className="ml-0.5 rounded bg-gray-100 px-1 py-0.5 text-[10px] font-medium">
                      {THICKNESS_LABELS[item.thickness_mm] || item.thickness_mm}
                    </span>
                  </span>
                  {item.current_location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-gray-400" />
                      {item.current_location}
                    </span>
                  )}
                </div>

                {/* Reserved info */}
                {item.status === 'reserved' && (item.reserved_by || item.reserved_case) && (
                  <div className="mt-1.5 rounded bg-orange-50 px-2 py-1 text-xs text-orange-700">
                    預定：{item.reserved_by || ''}
                    {item.reserved_case ? ` — ${item.reserved_case}` : ''}
                  </div>
                )}

                {/* Case number */}
                {item.case_number && (
                  <p className="mt-1 text-xs text-gray-400">案件：{item.case_number}</p>
                )}
              </div>
            </Link>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            disabled={currentPage === 1}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            上一頁
          </button>
          <span className="text-sm text-gray-500">
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setOffset(offset + LIMIT)}
            disabled={currentPage >= totalPages}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            下一頁
          </button>
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => navigate('/stock/create')}
        aria-label="新增餘料"
        className="fixed bottom-20 right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg hover:bg-primary/90"
      >
        <Plus className="h-6 w-6 text-white" />
      </button>
    </div>
  );
}
